import { observable, action } from "mobx";
import captchaHandlerDecorate from "../../utils/captcha-deco-util";
import { CAPTCHA_PRE_AUTH, XOYO_REGISTER } from "../../constants/urls";
import Services from "../../services";
import SmsCodeInputStore from "../sms-code-input/store";
import TinyInputStore from "../tiny-input/store";
import ToastStore from "../toast/store";
import { useEffect, useRef } from "preact/hooks";
import rsa from "@xfe/rsa-zt";
import { isMobile } from "../../utils/device-utils";
import { phoneNumber } from "../../utils/common-regex";
import $$tracker from "../../tracker";


class LoginStore {
  constructor({ onChangeContent, onClose, ...rest }) {
    this.onChangeContent = (type) => {
      isMobile() ? window.open(XOYO_REGISTER) : onChangeContent(type);
    };
    this.onClose = onClose;
    this.restProps = rest;
  }

  //登录配置
  @observable
  loginConfig = window.XPASS.loginConfig;

  //手机登录
  smsCodeInput = new SmsCodeInputStore();

  //账号框
  accountInputStore = new TinyInputStore();

  //密码框
  passwordInputStore = new TinyInputStore();

  //toast
  toast = new ToastStore();


  //toast内容
  @observable
  toastContent = {
    msg: ""
  };

  //当前登录方式 passport:金山通行证 phone:手机号码
  @observable currentTab = "passport";

  @observable client = "default";


  //金山通行证登录框配置
  @observable
  accountMsgConfig = [
    {
      key: "account",
      type: "text",
      placeholder: "账号/手机/邮箱",
      store: this.accountInputStore,
      onBlur: () => {
        this.account();
      }
    }, {
      key: "password",
      type: "password",
      placeholder: "密码",
      store: this.passwordInputStore,
      onBlur: () => {
        this.password();
      }
    }
  ];


  //是否锁定登录按钮
  isLockLogin = false;


  /**
   * 登录成功后回调
   */
  @observable
  onLoginSuccess = window.XPASS.onLoginSuccess;


  //登录方式切换
  @action.bound
  onTabClick(tab) {
    this.currentTab = tab;

    //当在逍遥登录输入手机号时, 这时候点击去手机登录. 自动填入手机号码
    if (tab === "phone") {
      if (phoneNumber.test(this.accountInputStore.value)) {
        this.smsCodeInput.tinyInputPhoneNum.value = this.accountInputStore.value;
      }
    }
  }

  /**
   * 更新警告提示
   * @param type
   * @param msg
   */
  setWarnTip(type, msg) {
    this.accountMsgConfig = this.accountMsgConfig.map((item) => item.key === type ? {
      ...item,
      warnTip: msg
    } : item);

  }

  /**
   * 账号
   * @param value
   */

  account() {
    if (!this.accountInputStore.value) {
      this.setWarnTip(this.accountMsgConfig[0].key, "请输入通行证账号");
      return;
    }
    this.setWarnTip(this.accountMsgConfig[0].key, "");
    return this.accountInputStore.value;
  }


  /**
   * 密码
   */
  password() {
    if (!this.passwordInputStore.value) {
      this.setWarnTip(this.accountMsgConfig[1].key, "请输入密码");
      return;
    }
    this.setWarnTip(this.accountMsgConfig[1].key, "");
    return this.passwordInputStore.value;
  }

  /**
   * 点击立即登录
   * @returns {Promise<void>}
   */
  @action.bound
  async onLoginClick(e) {
    e.preventDefault();

    if (this.currentTab === "passport" /* 金山通行证登录 */) {

      if (!this.account()) {
        this.accountInputStore.elementRef?.current?.focus();
      } else if (!this.password()) {
        this.passwordInputStore.elementRef?.current?.focus();
      } else {
        !this.isLockLogin && await this.onPassportLogin();
      }

    } else if (this.currentTab === "phone" /* 手机登录 */) {
      if (!this.smsCodeInput.phoneNum()) {
        this.smsCodeInput.tinyInputPhoneNum?.elementRef?.current?.focus();
      } else if (!this.smsCodeInput.phoneCode()) {
        this.smsCodeInput.tinyInputPhoneCode?.elementRef?.current?.focus();
      } else {
        !this.isLockLogin && await this.onLoginByCodeClick();
      }
    }

  }

  /**
   * 金山通行证登录
   * @param account
   * @param password
   * @param authParams
   * @returns {Promise<void>}
   */
  @action.bound
  @captchaHandlerDecorate()
  async onPassportLogin({ ...authParams }) {
    this.isLockLogin = true;
    const rsaParams = rsa.markRSAAndEncryptParamField({
      account: this.account(),
      password: this.password()
    }, ["password"]);
    await Services.login({ ...rsaParams, ...authParams,...this.restProps }).then((res) => {
      // console.info('login:', res);
      this.isLockLogin = false;
      this.toastContent = { msg: "登录成功" };
      $$tracker.loginSuccess();
      setTimeout(() => {
        this.onLoginSuccess && this.onLoginSuccess({ account: res?.account, res });
        this.onClose && this.onClose();
      },500);

    }).catch(err => {
      // console.info(err);
      this.isLockLogin = false;
      this.toastContent = { msg: err.message };
      $$tracker.loginFail(err?.code, err?.message);
    });
  }

  /**
   * 手机验证码登录
   * @returns {Promise<void>}
   */
  @action.bound
  async onLoginByCodeClick() {
    this.isLockLogin = true;
    const params = {
      account: this.smsCodeInput.phoneNum(),
      client: this.client,
      code: this.smsCodeInput.phoneCode()
    };
    await Services.loginByCode(params).then((res) => {
      this.isLockLogin = false;
      this.toastContent = { msg: "登录成功" };
      this.onLoginSuccess && this.onLoginSuccess({ account: res?.account, res });
      this.onClose && this.onClose();
    }).catch(err => {
      this.isLockLogin = false;
      this.toastContent = { msg: err.message };
    });
  }

  /**
   * wegame登录
   * @param platform
   * @returns {Promise<void>}
   */
  @action.bound
  async getAuthorizeUrl(platform) {
    if (platform === "wegame") {
      await Services.getWegameAuthorizeUrl({ platform: platform, redirect: window.location.href }).then(res => {
        if (res?.authorize_url) {
          window.location.href = res?.authorize_url;
        }
      }).catch(err => {
        // console.info('url', err);
        this.toastContent = { msg: err.message };
      });

    } else {
      await Services.getThirdAuthorize(platform).then(res => {
        if (res?.auth_url) {
          window.location.href = res?.auth_url;
        }
      }).catch(err => {
        // console.info('thirdUrl:', err);
        this.toastContent = { msg: err.message };
      });
    }

    if (platform === "wegame") {
      $$tracker.wegameClick();
    } else if (platform === "qq") {
      $$tracker.qqClick();
    }

  }

  use() {

    this.accountInputStore.elementRef = useRef();
    this.passwordInputStore.elementRef = useRef();
    this.smsCodeInput.tinyInputPhoneNum.elementRef = useRef();
    this.smsCodeInput.tinyInputPhoneCode.elementRef = useRef();


    useEffect(() => {
      this.smsCodeInput.type = "login";

    }, []);

    useEffect(() => {
      if (this.toastContent.msg) {
        this.toast.visible = true;
        this.toast.msg = this.toastContent.msg;

      }
    }, [this.toastContent]);

    useEffect(() => {
      if (this.loginConfig) {
        this.currentTab = this.loginConfig?.passportLoginType?.defaultType;
      }
    }, [this.loginConfig]);

  }
}

export default LoginStore;
